import { ICanvas, IChat, IChatRequest, IContentBlock, IMessage, ITokenUsage } from "@/lib/types";
import {
    fetchStreamResponse,
    getPresignedDownloadUrl,
    getSummary,
    stopChat,
    updateChat as daoUpdateChat,
} from "@/lib/dao";
import { parseStreamToChunks } from "@/lib/stream";
import { v7 as uuidv7 } from "uuid";
import { getFirstAssistantTextContent, getRandomThinkingVariation, TITLE_WORD_LIMIT } from "@/lib/utils";

// AgentCore permits only one invocation per session at a time. A new request sent before the
// previous session is released returns a transient "already processing" / ConflictException
// error; it is safe to retry the same request once the session frees up.
const isConcurrencyConflict = (message: string): boolean =>
    /already processing|concurrent invocation|conflictexception/i.test(message);

// Max automatic retries for the concurrency conflict before surfacing a friendly error.
const MAX_CONFLICT_RETRIES = 4;

export interface LoadingState {
    loading: boolean;
    thinking: boolean;
    thinkingLabel: string;
}

export interface StartStreamOptions {
    message?: IMessage;
    retryMessageIdx?: number;
}

/**
 * Callbacks a session needs from the host app. Read lazily via a getter so a
 * long-lived session always sees the latest closures (selectedModel, etc.)
 * without being recreated.
 */
export interface SessionCallbacks {
    getJwt: () => Promise<string>;
    onMessagesCommitted: (chatid: string, messages: IMessage[]) => void;
    onTitleGenerated: (chatid: string, title: string) => void;
    onCanvasOpen: (chatid: string, canvas: ICanvas) => void;
    selectedModel: string;
}

/**
 * An immutable view of a session's streaming state. A NEW object is produced on
 * every change so `useSyncExternalStore` can detect updates by identity; between
 * changes the same reference is returned (required — React calls getSnapshot on
 * every render and bails when the ref is unchanged).
 */
export interface StreamSnapshot {
    streamingBlocks: IContentBlock[];
    committedMessages: IMessage[];
    loadingState: LoadingState;
    isStreaming: boolean;
    streamReceiving: boolean;
    tokenUsage: ITokenUsage | null;
}

export interface StreamSession {
    chatid: string;
    getSnapshot: () => StreamSnapshot;
    subscribe: (listener: () => void) => () => void;
    start: (chat: IChat, options?: StartStreamOptions) => void;
    stop: (commitPartial?: boolean) => void;
    setLoadingState: (updater: LoadingState | ((prev: LoadingState) => LoadingState)) => void;
    clearTokenUsage: () => void;
    isStreaming: () => boolean;
    hasListeners: () => boolean;
    destroy: () => void;
}

const STOP_COOLDOWN_MS = 2500;
const STREAM_TIMEOUT_MS = 90_000;

/**
 * Create a self-contained streaming session for a single chat. All lifecycle
 * state (abort controller, accumulators, timers) is local to this closure, so
 * many sessions can stream concurrently without colliding. This is the per-chat
 * port of the former single-instance useStreamChat hook.
 */
export function createStreamSession(chatid: string, getCallbacks: () => SessionCallbacks): StreamSession {
    // ─── Subscription store ──────────────────────────────────────────────
    const listeners = new Set<() => void>();
    let snapshot: StreamSnapshot = {
        streamingBlocks: [],
        committedMessages: [],
        loadingState: { loading: false, thinking: false, thinkingLabel: getRandomThinkingVariation() },
        isStreaming: false,
        streamReceiving: false,
        tokenUsage: null,
    };

    const emit = () => {
        for (const listener of listeners) listener();
    };

    /** Replace the snapshot with a new object (identity change) and notify subscribers. */
    const commit = (partial: Partial<StreamSnapshot>) => {
        snapshot = { ...snapshot, ...partial };
        emit();
    };

    // ─── Setters that mirror the former React setState API ───────────────
    // Keeping these names lets the ported stream body below stay verbatim.
    const setStreamingBlocks = (blocks: IContentBlock[]) => commit({ streamingBlocks: blocks });
    const setCommittedMessages = (next: IMessage[] | ((prev: IMessage[]) => IMessage[])) =>
        commit({ committedMessages: typeof next === "function" ? next(snapshot.committedMessages) : next });
    const setIsStreamingState = (value: boolean) => commit({ isStreaming: value });
    const setStreamReceiving = (value: boolean) => commit({ streamReceiving: value });
    const setTokenUsage = (usage: ITokenUsage | null) => commit({ tokenUsage: usage });
    const setLoadingState = (updater: LoadingState | ((prev: LoadingState) => LoadingState)) =>
        commit({ loadingState: typeof updater === "function" ? updater(snapshot.loadingState) : updater });

    // ─── Per-session lifecycle state (was hook refs) ─────────────────────
    const abortControllerRef: { current: AbortController | null } = { current: null };
    const isStreamingRef = { current: false };
    const rafRef: { current: number | null } = { current: null };
    const pendingBlocksRef: { current: IContentBlock[] | null } = { current: null };
    const heartbeatTimeoutRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };
    // Timestamp of the last user-initiated stop; used to delay the next request
    // until AWS has released the previous AgentCore session (avoids ConflictException).
    const lastUserStopTimeRef = { current: 0 };
    // chatid of the active stream, captured in start so stop can tell the backend
    // which in-flight chat to cancel on an explicit user stop.
    const activeChatIdRef: { current: string | null } = { current: null };

    function scheduleStreamingUpdate(blocks: IContentBlock[]) {
        pendingBlocksRef.current = blocks;
        if (rafRef.current === null) {
            rafRef.current = requestAnimationFrame(() => {
                if (pendingBlocksRef.current) {
                    setStreamingBlocks(pendingBlocksRef.current);
                }
                rafRef.current = null;
            });
        }
    }

    function clearStreamingState() {
        pendingBlocksRef.current = null;
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        setStreamingBlocks([]);
    }

    function buildStreamingBlocks(contentBlocks: Map<number, IContentBlock>, role: string): IContentBlock[] {
        return Array.from(contentBlocks.entries())
            .sort(([a], [b]) => a - b)
            .map(([, block]) => ({ ...block, role: role as IContentBlock["role"] }));
    }

    /**
     * Stops the in-progress stream.
     *
     * @param commitPartial When true (user-initiated stop), the partial assistant
     *   content currently on screen is committed into the displayed message list,
     *   marked `stopped: true`, and KEPT visible. The fetch is then aborted with the
     *   `'user-stop'` reason so the stream loop terminates quietly without clearing
     *   the partial. When false (default — programmatic teardown), the legacy
     *   behavior runs: abort with `'cleanup'` and wipe all stream state.
     */
    const stop = (commitPartial: boolean = false) => {
        // User-initiated stop: snapshot and commit the partial answer BEFORE aborting,
        // so the dropped connection doesn't erase what was already rendered.
        if (commitPartial) {
            const partialBlocks = pendingBlocksRef.current;
            if (partialBlocks && partialBlocks.length > 0) {
                const partialMessage: IMessage = {
                    messageid: uuidv7(),
                    role: "assistant",
                    content: partialBlocks.map((block) => ({ ...block, role: undefined })),
                    stopped: true,
                };
                setCommittedMessages((prev) => [...prev, partialMessage]);
            }
            // Record stop time so the next start can wait for AWS session release.
            lastUserStopTimeRef.current = Date.now();

            // Signal the explicit stop out-of-band BEFORE aborting the fetch. A
            // dropped connection alone reads as a passive leave (backend keeps the
            // agent running and persists the full result); this tells the backend
            // to cancel the run and persist the partial instead. Best-effort: the
            // DAO swallows errors, so this never throws into the stop UX.
            const activeChatid = activeChatIdRef.current;
            if (activeChatid) {
                void getCallbacks()
                    .getJwt()
                    .then((jwt) => stopChat(jwt, activeChatid))
                    .catch(() => {
                        // getJwt failure is non-fatal: the local partial is already
                        // committed and the passive-leave path still persists server-side.
                    });
            }
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort(commitPartial ? "user-stop" : "cleanup");
            abortControllerRef.current = null;
        }
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
            heartbeatTimeoutRef.current = null;
        }
        isStreamingRef.current = false;
        pendingBlocksRef.current = null;
        setStreamingBlocks([]);
        setIsStreamingState(false);
        setStreamReceiving(false);
        setLoadingState({
            loading: false,
            thinking: false,
            thinkingLabel: getRandomThinkingVariation(),
        });

        // Only the legacy teardown path wipes the committed partial and token usage.
        if (!commitPartial) {
            setCommittedMessages([]);
            setTokenUsage(null);
        }
    };

    const start = (chat: IChat, options?: StartStreamOptions) => {
        // One stream per chat. (Concurrency is across sessions, so unlike the old
        // single-instance hook we do NOT abort any other chat's controller here.)
        if (isStreamingRef.current) return;
        isStreamingRef.current = true;
        setIsStreamingState(true);

        const callbacks = getCallbacks();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        // Track the active chat so an explicit stop(true) can tell the backend
        // which in-flight chat to cancel.
        activeChatIdRef.current = chat.chatid;

        // True only while THIS chain is still the active stream. Once a newer start
        // (or stop) runs, it replaces/nulls abortControllerRef — this chain is then
        // "superseded" and must not write shared state (isStreaming/streamReceiving),
        // or its late-arriving abort would clobber the stream that replaced it.
        const isCurrentStream = () => abortControllerRef.current === abortController;

        // Reset state for new stream
        setCommittedMessages([]);
        setTokenUsage(null);
        setStreamReceiving(false);
        clearStreamingState();
        setLoadingState({
            loading: false,
            thinking: true,
            thinkingLabel: getRandomThinkingVariation(),
        });

        let messagesCommitted = false;
        const allCommittedMessages: IMessage[] = [];

        callbacks
            .getJwt()
            .then(async (jwt) => {
                const request: IChatRequest = {
                    chatid: chat.chatid,
                    projectid: chat.projectid,
                    model: callbacks.selectedModel,
                };

                if (options?.retryMessageIdx !== undefined && options.retryMessageIdx !== null) {
                    request.retry_messageidx = options.retryMessageIdx;
                } else if (options?.message) {
                    request.message = options.message;
                }

                // If the user just stopped a stream, wait for AWS to release the
                // previous AgentCore session before sending the new request.
                const elapsed = Date.now() - lastUserStopTimeRef.current;
                const remaining = STOP_COOLDOWN_MS - elapsed;
                if (remaining > 0) {
                    await new Promise<void>((resolve) => setTimeout(resolve, remaining));
                    // Aborted during the cooldown wait — reset stream state before bailing
                    // so the session doesn't stay stuck "streaming" (mirrors the other exit paths).
                    if (abortController.signal.aborted) {
                        if (isCurrentStream()) {
                            isStreamingRef.current = false;
                            setIsStreamingState(false);
                            setStreamReceiving(false);
                        }
                        return;
                    }
                }

                let chatForTitle = chat;
                // Shared across retry attempts; the post-loop fallback reads activeBlocks.
                const activeBlocks = new Map<number, IContentBlock>();
                let lastAssembledMessageId: string | null = null;
                // Retry loop for the transient AgentCore concurrency conflict: re-attempt the
                // same request with exponential backoff until the previous session is released
                // (or MAX_CONFLICT_RETRIES is exhausted). The conflict always arrives as the
                // first SSE event, before any content commits, so each attempt restarts clean.
                let conflictRetry = false;
                for (let attempt = 0; ; attempt++) {
                    conflictRetry = false;
                    activeBlocks.clear();
                    lastAssembledMessageId = null;
                    console.log(`${Date.now()} starting stream`);
                    const stream = await fetchStreamResponse(jwt, request, abortController.signal);

                    const resetHeartbeatTimeout = () => {
                        if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
                        heartbeatTimeoutRef.current = setTimeout(() => {
                            abortController.abort("timeout");
                        }, STREAM_TIMEOUT_MS);
                    };
                    resetHeartbeatTimeout();

                    const parser = parseStreamToChunks(stream, resetHeartbeatTimeout);

                    function handleSubagentEvent(agentName: string, updater: (block: IContentBlock) => void) {
                        // 1. Try activeBlocks (sub-agent event arrived before messageStop)
                        for (const [, block] of activeBlocks) {
                            if (block.toolUse?.name === agentName) {
                                if (!block.subagentBlock) {
                                    block.subagentBlock = {
                                        agentName,
                                        reasoning: "",
                                        tools: [],
                                        text: "",
                                        isComplete: false,
                                    };
                                }
                                updater(block);
                                scheduleStreamingUpdate(buildStreamingBlocks(activeBlocks, "assistant"));
                                return;
                            }
                        }

                        // 2. Try committed messages (sub-agent event arrived after messageStop cleared activeBlocks)
                        for (let i = allCommittedMessages.length - 1; i >= 0; i--) {
                            const msg = allCommittedMessages[i];
                            if (msg.role === "assistant") {
                                const toolBlock = msg.content.find((b) => b.toolUse?.name === agentName);
                                if (toolBlock) {
                                    if (!toolBlock.subagentBlock) {
                                        toolBlock.subagentBlock = {
                                            agentName,
                                            reasoning: "",
                                            tools: [],
                                            text: "",
                                            isComplete: false,
                                        };
                                    }
                                    updater(toolBlock);
                                    setCommittedMessages([...allCommittedMessages]);
                                    return;
                                }
                            }
                        }

                        // 3. Fallback: standalone block in activeBlocks
                        const nextIdx = Math.max(-1, ...activeBlocks.keys()) + 1;
                        const newBlock: IContentBlock = {
                            subagentBlock: {
                                agentName,
                                reasoning: "",
                                tools: [],
                                text: "",
                                isComplete: false,
                            },
                        };
                        updater(newBlock);
                        activeBlocks.set(nextIdx, newBlock);
                        scheduleStreamingUpdate(buildStreamingBlocks(activeBlocks, "assistant"));
                    }

                    try {
                        let firstChunkReceived = false;
                        for await (const chunk of parser) {
                            resetHeartbeatTimeout();

                            if (!firstChunkReceived) {
                                firstChunkReceived = true;
                                setStreamReceiving(true);
                            }

                            // Backend error event
                            if (chunk.streamError) {
                                clearStreamingState();

                                // Transient AgentCore concurrency conflict: retry the same request
                                // (with backoff) instead of surfacing the raw error to the user.
                                // Keep `thinking: true` so the breathing indicator stays visible
                                // through the backoff + retry wait (this is a working state, not an error).
                                if (
                                    isConcurrencyConflict(chunk.streamError.message) &&
                                    attempt < MAX_CONFLICT_RETRIES
                                ) {
                                    conflictRetry = true;
                                    setStreamReceiving(false);
                                    break;
                                }

                                setLoadingState((prev) => ({
                                    loading: false,
                                    thinking: false,
                                    thinkingLabel: prev.thinkingLabel,
                                }));

                                const message = isConcurrencyConflict(chunk.streamError.message)
                                    ? "The assistant is still finishing the previous response. Please try again in a moment."
                                    : chunk.streamError.message;
                                const errorMessage: IMessage = {
                                    messageid: uuidv7(),
                                    role: "assistant" as const,
                                    content: [{ text: message }],
                                };
                                allCommittedMessages.push(errorMessage);
                                setCommittedMessages([...allCommittedMessages]);
                                if (isCurrentStream()) {
                                    isStreamingRef.current = false;
                                    setIsStreamingState(false);
                                    setStreamReceiving(false);
                                }
                                return;
                            }

                            // messageStart: new assistant turn begins
                            if (chunk.messageStart) {
                                activeBlocks.clear();
                                clearStreamingState();
                            }

                            // contentBlockStart: initialize a new block
                            if (chunk.contentBlockStart) {
                                const idx = chunk.contentBlockStart.contentBlockIndex;
                                const block: IContentBlock = {};
                                if (chunk.contentBlockStart.start.toolUse) {
                                    block.toolUse = {
                                        name: chunk.contentBlockStart.start.toolUse.name,
                                        toolUseId: chunk.contentBlockStart.start.toolUse.toolUseId || uuidv7(),
                                        input: "",
                                    };
                                }
                                activeBlocks.set(idx, block);
                                scheduleStreamingUpdate(buildStreamingBlocks(activeBlocks, "assistant"));
                            }

                            // contentBlockDelta: accumulate text/reasoning/toolUse input
                            if (chunk.contentBlockDelta) {
                                const idx = chunk.contentBlockDelta.contentBlockIndex;
                                let block = activeBlocks.get(idx);
                                if (!block) {
                                    block = {};
                                    activeBlocks.set(idx, block);
                                }
                                const delta = chunk.contentBlockDelta.delta;

                                if (delta.reasoningContent) {
                                    if (!block.reasoningContent) {
                                        block.reasoningContent = { reasoningText: { text: "" } };
                                    }
                                    if (delta.reasoningContent.text && block.reasoningContent.reasoningText) {
                                        block.reasoningContent.reasoningText.text += delta.reasoningContent.text;
                                    }
                                    if (delta.reasoningContent.signature && block.reasoningContent.reasoningText) {
                                        block.reasoningContent.reasoningText.signature =
                                            delta.reasoningContent.signature;
                                    }
                                }
                                if (delta.toolUse && block.toolUse) {
                                    block.toolUse.input += delta.toolUse.input;
                                }
                                if (delta.text) {
                                    block.text = (block.text || "") + delta.text;
                                }

                                scheduleStreamingUpdate(buildStreamingBlocks(activeBlocks, "assistant"));
                            }

                            // contentBlockStop: block finalized (keep in map for continued display)
                            if (chunk.contentBlockStop) {
                                // Block stays in activeBlocks for rendering until message commit
                            }

                            // Sub-agent events: accumulate into sub-agent block (handles both active and committed states)
                            if (chunk.subagentReasoning) {
                                handleSubagentEvent(chunk.subagentReasoning.agentName, (block) => {
                                    block.subagentBlock!.reasoning += chunk.subagentReasoning!.reasoningText;
                                });
                            }
                            if (chunk.subagentToolUse) {
                                handleSubagentEvent(chunk.subagentToolUse.agentName, (block) => {
                                    block.subagentBlock!.tools.push({
                                        toolName: chunk.subagentToolUse!.toolName,
                                        toolInput: chunk.subagentToolUse!.toolInput,
                                    });
                                });
                            }
                            if (chunk.subagentToolResult) {
                                handleSubagentEvent(chunk.subagentToolResult.agentName, (block) => {
                                    const pending = block.subagentBlock!.tools.find((t) => !t.status);
                                    if (pending) {
                                        pending.status = chunk.subagentToolResult!.status;
                                        pending.resultPreview = chunk.subagentToolResult!.contentPreview;
                                    }
                                });
                            }
                            if (chunk.subagentText) {
                                handleSubagentEvent(chunk.subagentText.agentName, (block) => {
                                    block.subagentBlock!.text += chunk.subagentText!.text;
                                });
                            }
                            if (chunk.subagentAuthRequired) {
                                const auth = chunk.subagentAuthRequired;
                                const authBlock: IContentBlock = {
                                    subagentAuthRequired: {
                                        agentName: auth.agentName,
                                        provider: auth.provider,
                                        authorizationUrl: auth.authorizationUrl,
                                    },
                                };

                                // 1. Try activeBlocks (auth event arrived before orchestrator's messageStop)
                                let attached = false;
                                for (const [, block] of activeBlocks) {
                                    if (block.toolUse?.name === auth.agentName) {
                                        const nextIdx = Math.max(-1, ...activeBlocks.keys()) + 1;
                                        activeBlocks.set(nextIdx, authBlock);
                                        scheduleStreamingUpdate(buildStreamingBlocks(activeBlocks, "assistant"));
                                        attached = true;
                                        break;
                                    }
                                }

                                // 2. Try committed messages (auth event arrived after orchestrator's messageStop)
                                if (!attached) {
                                    for (let i = allCommittedMessages.length - 1; i >= 0; i--) {
                                        const msg = allCommittedMessages[i];
                                        if (
                                            msg.role === "assistant" &&
                                            msg.content.some((b) => b.toolUse?.name === auth.agentName)
                                        ) {
                                            msg.content.push(authBlock);
                                            setCommittedMessages([...allCommittedMessages]);
                                            attached = true;
                                            break;
                                        }
                                    }
                                }
                            }

                            // messageStop: turn is done — commit assembled message
                            if (chunk.messageStop) {
                                if (activeBlocks.size > 0) {
                                    const assembledContent: IContentBlock[] = Array.from(activeBlocks.entries())
                                        .sort(([a], [b]) => a - b)
                                        .map(([, block]) => ({ ...block }));

                                    const assembledMessage: IMessage = {
                                        messageid: uuidv7(),
                                        role: "assistant",
                                        content: assembledContent,
                                    };

                                    lastAssembledMessageId = assembledMessage.messageid;
                                    activeBlocks.clear();
                                    clearStreamingState();
                                    messagesCommitted = true;

                                    allCommittedMessages.push(assembledMessage);
                                    setCommittedMessages([...allCommittedMessages]);

                                    if (chunk.messageStop.stopReason === "tool_use") {
                                        setLoadingState({
                                            loading: false,
                                            thinking: true,
                                            thinkingLabel: getRandomThinkingVariation(),
                                        });
                                    }
                                }
                            }

                            // Complete assistant message: upgrade temp ID
                            if (chunk.message?.role === "assistant") {
                                if (lastAssembledMessageId) {
                                    const idx = allCommittedMessages.findIndex(
                                        (m) => m.messageid === lastAssembledMessageId
                                    );
                                    if (idx !== -1) {
                                        allCommittedMessages[idx] = { ...(chunk.message as IMessage) };
                                        setCommittedMessages([...allCommittedMessages]);
                                    }
                                    lastAssembledMessageId = null;
                                } else if (!messagesCommitted) {
                                    // Defensive fallback: messageStop didn't fire
                                    activeBlocks.clear();
                                    clearStreamingState();
                                    messagesCommitted = true;
                                    allCommittedMessages.push(chunk.message as IMessage);
                                    setCommittedMessages([...allCommittedMessages]);
                                }
                                setLoadingState({
                                    loading: false,
                                    thinking: true,
                                    thinkingLabel: getRandomThinkingVariation(),
                                });
                            }

                            // Metadata: capture token usage
                            if (chunk.metadata?.usage) {
                                setTokenUsage({
                                    inputTokens: chunk.metadata.usage.inputTokens,
                                    outputTokens: chunk.metadata.usage.outputTokens,
                                });
                            }

                            // Complete user message (tool results): commit directly
                            if (chunk.message?.role === "user") {
                                lastAssembledMessageId = null;
                                messagesCommitted = true;
                                allCommittedMessages.push(chunk.message as IMessage);
                                setCommittedMessages([...allCommittedMessages]);

                                // Check for document creation tool result to open canvas
                                const docJson = chunk.message.content
                                    .find((block) =>
                                        block.toolResult?.content?.some(
                                            (c) => c.json?.key && c.json?.filename && c.json?.url
                                        )
                                    )
                                    ?.toolResult?.content?.find(
                                        (c) => c.json?.key && c.json?.filename && c.json?.url
                                    )?.json;
                                if (docJson) {
                                    const presignedUrl = await getPresignedDownloadUrl(
                                        jwt,
                                        docJson.key,
                                        undefined,
                                        false
                                    );
                                    const canvas: ICanvas = {
                                        title: docJson.filename,
                                        key: docJson.key,
                                        url: presignedUrl,
                                    };
                                    callbacks.onCanvasOpen(chat.chatid, canvas);
                                }
                                setLoadingState({
                                    loading: false,
                                    thinking: true,
                                    thinkingLabel: getRandomThinkingVariation(),
                                });
                            }
                        }
                    } catch (ex) {
                        // 'cleanup' = teardown (wipes elsewhere); 'user-stop' = user clicked stop
                        // (partial already committed by stop). Both terminate quietly.
                        if (
                            abortController.signal.aborted &&
                            (abortController.signal.reason === "cleanup" ||
                                abortController.signal.reason === "user-stop")
                        ) {
                            // Only reset shared state if this is still the active stream; a
                            // superseded chain must not stomp the stream that replaced it.
                            if (isCurrentStream()) {
                                isStreamingRef.current = false;
                                setIsStreamingState(false);
                                setStreamReceiving(false);
                            }
                            return;
                        }
                        throw ex;
                    }

                    // Concurrency conflict: back off (1s, 2s, 4s, 8s — capped) then retry the
                    // same request. Bail if the stream was aborted during the wait.
                    if (conflictRetry) {
                        if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
                        const backoffMs = Math.min(1000 * 2 ** attempt, 8000);
                        await new Promise<void>((resolve) => setTimeout(resolve, backoffMs));
                        if (abortController.signal.aborted) {
                            if (isCurrentStream()) {
                                isStreamingRef.current = false;
                                setIsStreamingState(false);
                                setStreamReceiving(false);
                            }
                            return;
                        }
                        continue;
                    }

                    break;
                }

                if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
                console.log(`${Date.now()} ended stream`);
                setLoadingState((prev) => ({ loading: false, thinking: false, thinkingLabel: prev.thinkingLabel }));

                // Fallback: if stream ended but nothing was committed, assemble from remaining blocks
                if (!messagesCommitted && activeBlocks.size > 0) {
                    const assembledContent: IContentBlock[] = Array.from(activeBlocks.entries())
                        .sort(([a], [b]) => a - b)
                        .map(([, block]) => ({ ...block }));
                    const assembledMessage: IMessage = {
                        messageid: uuidv7(),
                        role: "assistant",
                        content: assembledContent,
                    };
                    activeBlocks.clear();
                    clearStreamingState();
                    messagesCommitted = true;
                    allCommittedMessages.push(assembledMessage);
                    setCommittedMessages([...allCommittedMessages]);
                }

                // Build final messages list for context sync
                const finalMessages = [...(chat.messages || []), ...allCommittedMessages];
                chatForTitle = { ...chat, messages: finalMessages };

                // Sync streamed messages back to chats context
                if (messagesCommitted) {
                    callbacks.onMessagesCommitted(chat.chatid, finalMessages);
                }
                setCommittedMessages([]);

                // Only reset shared stream state if this chain is still the active stream;
                // a superseded chain reaching completion must not clobber the current one.
                if (isCurrentStream()) {
                    isStreamingRef.current = false;
                    setIsStreamingState(false);
                    setStreamReceiving(false);
                }

                // Title generation (runs after stream is marked complete — safe because
                // title gen is independent of stream state and doesn't need the guard)
                if (chat.title === undefined) {
                    const firstAssistantMessage = getFirstAssistantTextContent(finalMessages);
                    if (firstAssistantMessage) {
                        const summary = await getSummary(jwt, firstAssistantMessage, TITLE_WORD_LIMIT);
                        const title = summary.summary;
                        console.log(`${Date.now()} updating title: ${title}`);
                        callbacks.onTitleGenerated(chatForTitle.chatid, title);
                        daoUpdateChat(jwt, { chatid: chatForTitle.chatid, title });
                    }
                }
            })
            .catch((ex) => {
                if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);

                // Superseded chain (a newer start/stop already replaced this one):
                // bail quietly without touching shared state or committing an error message —
                // the stream that replaced it owns the UI now.
                if (!isCurrentStream()) {
                    return;
                }

                if (
                    abortController.signal.aborted &&
                    (abortController.signal.reason === "cleanup" || abortController.signal.reason === "user-stop")
                ) {
                    isStreamingRef.current = false;
                    setIsStreamingState(false);
                    setStreamReceiving(false);
                    return;
                }

                console.error("Stream error:", ex);
                setLoadingState((prev) => ({ loading: false, thinking: false, thinkingLabel: prev.thinkingLabel }));
                clearStreamingState();

                const isTimeout = abortController.signal.aborted && abortController.signal.reason === "timeout";
                const errorMessage = isTimeout
                    ? "The response timed out. Please try again."
                    : "I encountered an error processing your request. Please try again.";

                if (!messagesCommitted) {
                    const errorMsg: IMessage = {
                        messageid: uuidv7(),
                        role: "assistant" as const,
                        content: [{ text: errorMessage }],
                    };
                    allCommittedMessages.push(errorMsg);
                    setCommittedMessages([...allCommittedMessages]);
                }

                isStreamingRef.current = false;
                setIsStreamingState(false);
                setStreamReceiving(false);
            });
    };

    const destroy = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort("cleanup");
            abortControllerRef.current = null;
        }
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
            heartbeatTimeoutRef.current = null;
        }
        isStreamingRef.current = false;
        listeners.clear();
    };

    return {
        chatid,
        getSnapshot: () => snapshot,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        start,
        stop,
        setLoadingState,
        clearTokenUsage: () => setTokenUsage(null),
        isStreaming: () => isStreamingRef.current,
        hasListeners: () => listeners.size > 0,
        destroy,
    };
}
